import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine,
} from "recharts";
import { ChartLineUp, TrendUp, TrendDown, Coin, ClockCounterClockwise, Warning, ArrowClockwise } from "@phosphor-icons/react";
import { formatEuros } from "@/components/PriceGuard";

/**
 * Admin-only Rentabilité panel. Fetches /admin/profitability and renders:
 *   - Global KPI row (revenue, cost, margin, hours, chantiers)
 *   - Monthly stacked bar chart (revenue vs cost, margin line)
 *   - Top 10 rentables + Top 10 déficitaires
 */
const CURRENT_YEAR = new Date().getFullYear();

export default function ProfitabilityPanel() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [month, setMonth] = useState(null); // null = whole year
  const [state, setState] = useState({ loading: true, data: null, error: null });

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const params = { year };
      if (month != null) params.month = month;
      const r = await api.get("/admin/profitability", { params });
      setState({ loading: false, data: r.data, error: null });
    } catch (err) {
      setState({ loading: false, data: null, error: formatApiError(err) });
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => { load(); }, [year, month]);  // reload on period change

  const d = state.data;
  const top = useMemo(() => (d?.rows || []).slice(0, 10), [d]);
  const bottom = useMemo(() => (d?.rows || []).slice().sort((a, b) => a.margin - b.margin).slice(0, 10), [d]);

  return (
    <div className="space-y-4" data-testid="profitability-panel">
      {/* Header + period picker */}
      <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 mr-auto font-display font-semibold text-sm">
          <ChartLineUp size={16} weight="duotone" className="text-[#7CB2FF]" />
          Rentabilité — {month == null ? `Année ${year}` : `${MONTH_LABELS[month - 1]} ${year}`}
        </div>
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" data-testid="profit-year">
          {Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - 4 + i).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={month ?? ""} onChange={(e) => setMonth(e.target.value === "" ? null : Number(e.target.value))} className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-xs" data-testid="profit-month">
          <option value="">Année complète</option>
          {MONTH_LABELS.map((l, i) => <option key={i} value={i + 1}>{l}</option>)}
        </select>
        <button onClick={load} className="text-xs px-3 py-1.5 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 flex items-center gap-1.5 text-white/70">
          <ArrowClockwise size={11} weight="bold" /> Rafraîchir
        </button>
      </div>

      {state.error && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-3 flex items-center gap-2 text-red-300 text-sm">
          <Warning size={16} weight="duotone" /> {state.error}
        </div>
      )}

      {state.loading && !d ? (
        <div className="text-white/50 text-sm py-10 text-center">Calcul en cours…</div>
      ) : d && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="profit-kpis">
            <Kpi icon={<Coin size={16} weight="duotone" />} label="Chiffre d'affaires" value={formatEuros(d.totals.revenue)} accent="#10B981" sub="factures encaissées Axonaut" />
            <Kpi icon={<ClockCounterClockwise size={16} weight="duotone" />} label="Coût main d'œuvre" value={formatEuros(d.totals.cost)} accent="#F59E0B" sub={`${d.totals.hours} h · ${formatEuros(d.hourly_rate)}/h`} />
            <Kpi
              icon={d.totals.margin >= 0 ? <TrendUp size={16} weight="duotone" /> : <TrendDown size={16} weight="duotone" />}
              label="Marge"
              value={formatEuros(d.totals.margin)}
              accent={d.totals.margin >= 0 ? "#005CFF" : "#EF4444"}
              sub={d.totals.margin_pct != null ? `${d.totals.margin_pct}%` : "—"}
            />
            <Kpi
              icon={<Warning size={16} weight="duotone" />}
              label="CA non attribué"
              value={formatEuros(d.totals.unattributed_revenue || 0)}
              accent="#94A3B8"
              sub="clients sans chantier lié"
            />
          </div>

          {/* Monthly chart (only for year view) */}
          {month == null && (
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid="profit-chart">
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">Rentabilité par mois</div>
              <div style={{ width: "100%", height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={d.monthly} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: "#6B7280", fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                    <Tooltip
                      contentStyle={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 12, color: "#fff", fontSize: 12 }}
                      formatter={(v, name) => [formatEuros(v), name]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, color: "#9CA3AF" }} />
                    <ReferenceLine y={0} stroke="rgba(255,255,255,0.2)" />
                    <Bar dataKey="revenue" name="CA" fill="#10B981" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="cost" name="Coût" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="margin" name="Marge" fill="#005CFF" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top / Bottom lists */}
          <div className="grid md:grid-cols-2 gap-3">
            <RankSection title="Top 10 chantiers rentables" rows={top} accent="#10B981" testid="profit-top" />
            <RankSection title="Top 10 déficitaires" rows={bottom} accent="#EF4444" testid="profit-bottom" />
          </div>
        </>
      )}
    </div>
  );
}

const MONTH_LABELS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Août", "Sep", "Oct", "Nov", "Déc"];

function Kpi({ icon, label, value, sub, accent }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 flex items-center gap-1">{icon} {label}</div>
      <div className="font-display font-bold text-lg md:text-xl" style={{ color: accent }}>{value}</div>
      {sub && <div className="text-[11px] text-white/50 mt-0.5">{sub}</div>}
    </div>
  );
}

function RankSection({ title, rows, accent, testid }) {
  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2">{title}</div>
      {rows.length === 0 ? (
        <div className="text-xs text-white/40 italic py-2">Aucun chantier sur la période.</div>
      ) : (
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={r.chantier_id} className="flex items-center gap-2 bg-white/[0.02] border border-white/5 rounded-lg px-3 py-2">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium truncate">{r.chantier_name}</div>
                <div className="text-[10px] text-white/45 truncate">
                  {r.client_name || "—"} · {r.hours}h · CA {formatEuros(r.revenue)}
                  {r.revenue_missing && <span className="ml-1 text-white/40">·</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono text-sm font-semibold" style={{ color: accent }}>{formatEuros(r.margin)}</div>
                {r.margin_pct != null && <div className="text-[10px] text-white/40">{r.margin_pct}%</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
